import React, { useEffect, useMemo, useState } from 'react';
import { Store } from '../../interfaces/store.interface';
import { employeeLabelWithFullName, EmployeeOption, useEmployeeSearch, withSelected } from '../common/EmployeeAutocomplete';
import { createStore, updateStore, deleteStore, getStoresByInstitutionId, deactivateStore, activateStore, setStoreInCharge } from '../../services/store.service';
import { Modal, Button, Box, Tooltip, IconButton, TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete } from '@mui/material';
import { Delete, Add, Edit } from '@mui/icons-material';
import {AuthService} from "../../services/auth.service";
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { MasterDataStatusFilter } from '../../interfaces/masterDataStatus.interface';
import { ActiveStatusChip, DeactivateDialog, StatusFilterField } from '../common/MasterDataStatus';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const StoreList: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.STORE_MANAGE);
    const [status, setStatus] = useState<MasterDataStatusFilter>('ACTIVE');
    const [toDeactivate, setToDeactivate] = useState<Store | null>(null);
    const isSystemAdmin = AuthService.getCurrentUser()?.roles?.includes('SystemAdmin');
    const [stores, setStores] = useState<Store[]>([]);
    const [loading, setLoading] = useState(true);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [formData, setFormData] = useState({
        storeName: '',
        address: '',
        description: '',
        institutionId: ''
    });
    // Store in charge: the people who see and work in this store (Store Manager / Store Staff are limited to these)
    const [inCharge, setInCharge] = useState<EmployeeOption[]>([]);
    const {
        options: userOptions,
        loading: userSearchLoading,
        inputValue: userInput,
        handleInputChange: handleUserInputChange,
    } = useEmployeeSearch<EmployeeOption>({
        enabled: isFormModalOpen,
        activeOnly: true,
        onError: (err) => toast.showApiError(err, 'Failed to search users'),
    });
    const inChargeOptions = useMemo(() => withSelected(userOptions, inCharge), [userOptions, inCharge]);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    const fetchStores = async (page: number) => {
        try {
            setLoading(true);
            const response = await getStoresByInstitutionId(page, 10, status);
            setStores(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
            console.error('Error fetching stores:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores(0);
    }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleActivate = async (store: Store) => {
        try {
            await activateStore(store.id!);
            toast.showSuccess('Store activated');
            await fetchStores(pageInfo.currentPage);
        } catch (error) {
            toast.showApiError(error, 'Failed to activate store');
        }
    };

    const handleOpenFormModal = (store?: Store) => {
        if (store) {
            setSelectedStore(store);
            setFormData({
                storeName: store.storeName,
                address: store.address,
                description: store.description,
                institutionId: store.institutionId || ''
            });
            setInCharge((store.inCharge ?? []).map(u => ({ id: u.id, username: u.username, fullName: u.name })));
        } else {
            setSelectedStore(null);
            setInCharge([]);
            setFormData({
                storeName: '',
                address: '',
                description: '',
                institutionId: ''
            });
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedStore(null);
        setFormData({
            storeName: '',
            address: '',
            description: '',
            institutionId: ''
        });
    };

    const handleOpenDeleteModal = (store: Store) => {
        setSelectedStore(store);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedStore(null);
    };

    const handleDelete = async () => {
        if (!selectedStore) return;

        try {
            await deleteStore(selectedStore.id!);
            await fetchStores(pageInfo.currentPage);
            handleCloseDeleteModal();
        } catch (error) {
            console.error('Error deleting store:', error);
            toast.showApiError(error, 'Failed to delete store');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const inChargeIds = inCharge.map(u => u.id);
            if (selectedStore) {
                await updateStore(selectedStore.id!, formData);
                const before = (selectedStore.inCharge ?? []).map(u => u.id).sort().join(',');
                if (before !== [...inChargeIds].sort().join(',')) {
                    await setStoreInCharge(selectedStore.id!, inChargeIds);
                }
                toast.showSuccess('Store updated');
            } else {
                formData.institutionId = AuthService.getSelectedInstitution();
                const created = await createStore(formData);
                if (created?.id && inChargeIds.length > 0) {
                    await setStoreInCharge(created.id, inChargeIds);
                }
                toast.showSuccess('Store created');
            }
            await fetchStores(pageInfo.currentPage);
            handleCloseFormModal();
        } catch (error) {
            console.error('Error saving store:', error);
            toast.showApiError(error, 'Failed to save store');
        }
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="store.storeList.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Store List</h2>
                {canManage && (
                    <Button
                        data-help="store.storeList.create"
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={() => handleOpenFormModal()}
                    >
                        Create Store
                    </Button>
                )}
            </div>

            <Box data-help-section="store.storeList.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <StatusFilterField value={status} onChange={setStatus} />
            </Box>

            <div style={{ marginBottom: '20px' }}>
                Total Stores: {pageInfo.totalElements}
            </div>

            <div data-help-section="store.storeList.table">
            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }} data-testid="stores-table">
                <thead>
                    <tr>
                        <th data-help="store.storeList.col.name" style={tableHeaderStyle}>Store Name</th>
                        <th data-help="store.storeList.col.address" style={tableHeaderStyle}>Address</th>
                        <th data-help="store.storeList.col.description" style={tableHeaderStyle}>Description</th>
                        <th data-help="store.storeList.col.inCharge" style={tableHeaderStyle}>In charge</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th data-help="store.storeList.col.actions" style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {stores.map(store => (
                        <tr key={store.id} data-testid={`store-row-${store.id}`}>
                            <td style={tableCellStyle}>{store.storeName}</td>
                            <td style={tableCellStyle}>{store.address}</td>
                            <td style={tableCellStyle}>{store.description}</td>
                            <td style={tableCellStyle} data-testid={`store-in-charge-${store.id}`}>
                                {(store.inCharge ?? []).map(u => u.name).join(', ') || '-'}
                            </td>
                            <td style={tableCellStyle} data-help="store.storeList.status"><ActiveStatusChip active={store.active} reason={store.deactivationReason} /></td>
                            <td style={tableCellStyle}>
                                {canManage && (<Tooltip title="Edit Store" arrow>
                                    <IconButton
                                        data-help="store.storeList.edit"
                                        size="small"
                                        onClick={() => handleOpenFormModal(store)}
                                        color="primary"
                                        aria-label="Edit Store"
                                    >
                                        <Edit />
                                    </IconButton>
                                </Tooltip>)}
                                {canManage && (store.active === false ? (
                                    <Button data-help="store.storeList.activate" size="small" onClick={() => handleActivate(store)}>Activate</Button>
                                ) : (
                                    <Button data-help="store.storeList.deactivate" size="small" color="warning" onClick={() => setToDeactivate(store)}>Deactivate</Button>
                                ))}
                                {isSystemAdmin && (
                                    <Tooltip title="Delete Store" arrow>
                                        <IconButton
                                            data-help="store.storeList.delete"
                                            size="small"
                                            onClick={() => handleOpenDeleteModal(store)}
                                            color="error"
                                            aria-label="Delete Store"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </ScrollableTable>
            </div>

            {/* Form Modal */}
            <Modal
                data-help-page="store.storeForm.page" open={isFormModalOpen}
                onClose={handleCloseFormModal}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1
                }}>
                    <h2>{selectedStore ? 'Edit Store' : 'Create Store'}</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            data-help="store.storeForm.field.name" label="Store Name"
                            value={formData.storeName}
                            onChange={(e) => setFormData({ ...formData, storeName: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            data-help="store.storeForm.field.address" label="Address"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            data-help="store.storeForm.field.description" label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            required
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <Autocomplete<EmployeeOption, true, false, false>
                            multiple
                            data-help="store.storeForm.field.inCharge"
                            options={inChargeOptions}
                            getOptionLabel={employeeLabelWithFullName}
                            value={inCharge}
                            onChange={(_, newValue) => setInCharge(newValue)}
                            onInputChange={handleUserInputChange}
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            loading={userSearchLoading}
                            noOptionsText={userSearchLoading ? 'Loading\u2026' : userInput ? 'No users found' : 'No users'}
                            renderInput={(params) => (
                                <TextField {...params} label="In charge" placeholder="Search users..."
                                    helperText="People who see and work in this store. Leave empty if only the store head and admins use it." margin="normal" />
                            )}
                        />
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button data-help="store.storeForm.cancel" onClick={handleCloseFormModal}>
                                Cancel
                            </Button>
                            <Button data-help="store.storeForm.save" type="submit" variant="contained" color="primary">
                                {selectedStore ? 'Update' : 'Create'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            <DeactivateDialog
                open={toDeactivate !== null}
                noun="store"
                name={toDeactivate?.storeName ?? ''}
                onDeactivate={async (reason) => {
                    try {
                        return await deactivateStore(toDeactivate!.id!, reason);
                    } catch (e) {
                        toast.showApiError(e, 'Failed to deactivate store');
                        throw e;
                    }
                }}
                onClose={(changed) => { setToDeactivate(null); if (changed) fetchStores(pageInfo.currentPage); }}
            />

            {/* Delete Confirmation Modal */}
            {isSystemAdmin && (
                <Modal
                    data-help-page="store.storeList.deleteModal" open={isDeleteModalOpen}
                    onClose={handleCloseDeleteModal}
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 1
                    }}>
                        <h2>Delete Store</h2>
                        <p>Are you sure you want to delete this store?</p>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button data-help="store.storeList.deleteCancel" onClick={handleCloseDeleteModal}>
                                Cancel
                            </Button>
                            <Button
                                data-help="store.storeList.deleteConfirm"
                                onClick={handleDelete}
                                variant="contained"
                                color="error"
                            >
                                Delete
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            )}
        </div>
    );
};

export default StoreList;
